import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Home, LayoutDashboard, FolderClock, CreditCard, Cpu, User as UserIcon,
  Info, HelpCircle, Mail, LogOut, Sparkles, Menu, X, Shield
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";

const PLAN_BADGE = {
  free: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  pro: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  business: "bg-purple-500/20 text-purple-300 border-purple-500/40",
};

const NAV = [
  { to: "/", label: "Home", icon: Home },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, auth: true },
  { to: "/history", label: "History", icon: FolderClock, auth: true },
  { to: "/pricing", label: "Pricing", icon: CreditCard },
  { to: "/ai", label: "AI Technology", icon: Cpu },
  { to: "/profile", label: "Profile", icon: UserIcon, auth: true },
  { to: "/about", label: "About", icon: Info },
  { to: "/faq", label: "FAQ", icon: HelpCircle },
  { to: "/contact", label: "Contact", icon: Mail },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const items = NAV.filter(n => !n.auth || user);

  return (
    <>
      <button
        data-testid="sidebar-toggle"
        onClick={() => setOpen(o => !o)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg glass-strong"
      >
        {open ? <X size={20}/> : <Menu size={20}/>}
      </button>

      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 flex flex-col glass-strong border-r border-white/[0.08] transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <Link to="/" className="flex items-center gap-2.5 px-6 py-6 border-b border-white/[0.06]" data-testid="logo-link">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center glow-primary">
            <Sparkles size={18} className="text-white"/>
          </div>
          <div>
            <div className="text-lg font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>HumanizeAI</div>
            <div className="text-[10px] text-zinc-400 tracking-[0.25em] uppercase">Document Humanizer</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-4 overflow-y-auto scroll-slim">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 my-0.5 rounded-xl text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-indigo-500/15 text-white border border-indigo-500/30"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon size={17}/>
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}

          {user?.isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors duration-200 border ${
                  isActive ? "bg-red-500/15 text-red-300 border-red-500/40" : "border-red-500/20 text-red-300 hover:bg-red-500/10"
                }`
              }
            >
              <Shield size={17}/>
              <span className="font-medium">Admin Panel</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-white/[0.06] space-y-3">
          {user ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg glass">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-xs font-bold">
                  {(user.displayName || user.email)[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{user.displayName || user.email}</div>
                  <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded border ${PLAN_BADGE[user.plan] || PLAN_BADGE.free}`}>
                    {(user.plan || "free").toUpperCase()}
                  </span>
                </div>
              </div>
              <Button onClick={logout} variant="outline" className="w-full" data-testid="logout-btn">
                <LogOut size={14} className="mr-2"/> Logout
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Link to="/login" data-testid="sidebar-login-btn">
                <Button variant="outline" className="w-full">Login</Button>
              </Link>
              <Link to="/register" data-testid="sidebar-register-btn">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700">Sign up</Button>
              </Link>
            </div>
          )}
        </div>
      </aside>
      {open && <div className="lg:hidden fixed inset-0 z-30 bg-black/60" onClick={()=>setOpen(false)}/>}
    </>
  );
}
